import React, { useEffect } from 'react';
import { Warning, X } from '@phosphor-icons/react';
import { Button } from './Button';
interface ConfirmDialogProps {
  open: boolean;
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'default' | 'danger';
  onConfirm: () => void;
  onCancel: () => void;
  // L8.4 — when true, the modal centers within the dashboard content area
  // (offsets by the sidebar width on desktop). Defaults to false so global
  // call sites (marketplace, etc.) keep centering to the full viewport.
  dashboardOffset?: boolean;
}
export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel = 'Confirmă',
  cancelLabel = 'Anulează',
  variant = 'default',
  onConfirm,
  onCancel,
  dashboardOffset = false
}: ConfirmDialogProps) {
  // Close on ESC
  useEffect(() => {
    if (!open) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [open, onCancel]);
  if (!open) return null;
  const iconColor = variant === 'danger' ? 'text-red-1' : 'text-lime-1';
  const iconBg = variant === 'danger' ? 'bg-red-4' : 'bg-lime-4';
  return <div className={`fixed inset-0 z-[100] flex items-center justify-center p-4 animate-in fade-in duration-200 ${dashboardOffset ? 'lg:pl-[16rem]' : ''}`} role="dialog" aria-modal="true" aria-labelledby="confirm-dialog-title" data-id="element-2831">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-dark-1/80 backdrop-blur-sm" onClick={onCancel} data-id="element-2832" />

      {/* Dialog */}
      <div className="relative w-full max-w-sm bg-dark-2 border border-dark-3 rounded-2xl shadow-2xl animate-in zoom-in-95 slide-in-from-bottom-4 duration-200" data-id="element-2833">
        <button onClick={onCancel} aria-label="Închide" className="absolute top-3 right-3 p-1.5 text-light-3 hover:text-light-1 hover:bg-dark-3 rounded-md transition-colors" data-id="element-2834">
          <X className="w-4 h-4" weight="bold" data-id="element-2835" />
        </button>

        <div className="p-6" data-id="element-2836">
          <div className={`w-12 h-12 rounded-full ${iconBg} flex items-center justify-center mb-4`} data-id="element-2837">
            <Warning className={`w-6 h-6 ${iconColor}`} weight="fill" data-id="element-2838" />
          </div>

          <h3 id="confirm-dialog-title" className="text-lg font-bold text-light-1" data-id="element-2839">
            {title}
          </h3>

          {description && <p className="text-sm text-light-3 mt-2 leading-relaxed" data-id="element-2840">
              {description}
            </p>}

          <div className="flex gap-3 mt-6" data-id="element-2841">
            <Button variant="outline" onClick={onCancel} className="flex-1 bg-transparent border-dark-3 text-light-2 hover:bg-dark-3 hover:text-light-1" data-id="element-2842">
              {cancelLabel}
            </Button>
            <Button onClick={onConfirm} className={variant === 'danger' ? 'flex-1 bg-red-1 text-light-1 hover:opacity-90' : 'flex-1 bg-lime-1 text-dark-1 hover:bg-lime-2 font-semibold'} data-id="element-2843">
              {confirmLabel}
            </Button>
          </div>
        </div>
      </div>
    </div>;
}